import axios from "axios";

/**
 * Textos que a nest devolve quando NÃO quer expor o motivo real: em produção
 * todo 5xx vira "Internal server error" e um 4xx cuja mensagem parece SQL ou
 * stack trace vira "Bad request". Nenhum deles serve para a pessoa — o
 * `fallback` em pt-BR do call site é sempre melhor.
 */
const OPAQUE_API_MESSAGES: ReadonlySet<string> = new Set([
    "Internal server error",
    "Bad request",
    "Bad Request",
]);

/**
 * Normaliza a mensagem de erro da API, que pode vir como string única ou como
 * array de validações. Ponto único usado pelo `getApiErrorMessage` (axios) e
 * pelo BFF de auth (fetch). Cai no `fallback` quando não há texto legível —
 * inclusive quando a nest mandou um dos textos genéricos de produção.
 */
export function normalizeApiMessage(raw: unknown, fallback: string): string {
    if (Array.isArray(raw)) {
        const joined = raw.filter((part) => typeof part === "string" && part).join(" ");
        return joined && !OPAQUE_API_MESSAGES.has(joined) ? joined : fallback;
    }
    if (typeof raw === "string" && raw && !OPAQUE_API_MESSAGES.has(raw)) return raw;
    return fallback;
}

/** Corpo de erro como a nest envelopa (`{ error: { statusCode, message, details? } }`). */
type ApiErrorPayload = {
    error?:
        | string
        | string[]
        | { message?: string | string[]; details?: Record<string, unknown> };
};

function axiosPayload(error: unknown): ApiErrorPayload | undefined {
    return axios.isAxiosError(error) ? (error.response?.data as ApiErrorPayload | undefined) : undefined;
}

/**
 * Extrai a mensagem exibível de um erro, seja ele:
 * - AuthError/Error comum (BFF via fetch) — já traz `message` pronto;
 * - AxiosError da nest — mensagem no envelope `data.error.message`
 *   (ou `data.error` string / array de validações).
 *
 * Cai no `fallback` quando não há mensagem legível. Um 5xx NUNCA mostra o
 * texto do servidor: em produção ele é sempre "Internal server error".
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? 0;
        if (status >= 500) return fallback;

        const payload = axiosPayload(error);
        const raw =
            payload && typeof payload.error === "object" && !Array.isArray(payload.error)
                ? payload.error.message
                : payload?.error;

        return normalizeApiMessage(raw, fallback);
    }

    if (error instanceof Error && error.message) {
        return normalizeApiMessage(error.message, fallback);
    }
    return fallback;
}

/**
 * Status HTTP da resposta, quando o servidor respondeu. `undefined` para
 * timeout/queda de rede e para erros que não vieram do axios — e também para
 * o `AuthError` do BFF (que carrega o status em campo próprio).
 */
export function getApiErrorStatus(error: unknown): number | undefined {
    if (axios.isAxiosError(error)) return error.response?.status;
    if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status?: unknown }).status;
        return typeof status === "number" ? status : undefined;
    }
    return undefined;
}

/**
 * `error.details` do envelope da nest. Em produção só um conjunto fixo de
 * chaves chega aqui (errors, problematicProducts, unavailableProducts,
 * errorCode, errorType, canRetry, suggestions, …); qualquer outra vem vazia.
 */
export function getApiErrorDetails<T extends Record<string, unknown>>(error: unknown): T | undefined {
    const payload = axiosPayload(error);
    if (payload && typeof payload.error === "object" && !Array.isArray(payload.error)) {
        return payload.error.details as T | undefined;
    }
    return undefined;
}

/**
 * O servidor RESPONDEU (mesmo que com 4xx/5xx)?
 *
 * Distingue "a requisição falhou e sabemos disso" de "não sabemos o que
 * aconteceu" (timeout, queda de rede, requisição abortada). A diferença importa
 * em operações não-idempotentes: com resposta, o desfecho é conhecido e um
 * retry é um pedido novo; SEM resposta, a operação pode ter sido concluída no
 * back e o retry precisa reusar a chave de idempotência para não duplicar.
 */
export function hasServerResponse(error: unknown): boolean {
    return axios.isAxiosError(error) && Boolean(error.response);
}
